export type SequencedMessage = {
  id: string
  seq: number
  readAt?: Date | null
}

export type ConnectionMessageTimeline<Message extends SequencedMessage> = {
  connectionId: string
  messages: Message[]
  olderHasMore: boolean | null
  peerReadThroughSeq: number
}

export function createMessageTimeline<Message extends SequencedMessage>(
  connectionId: string,
): ConnectionMessageTimeline<Message> {
  return {
    connectionId,
    messages: [],
    olderHasMore: null,
    peerReadThroughSeq: 0,
  }
}

export function mergeSequencedMessages<Message extends SequencedMessage>(
  current: readonly Message[],
  incoming: readonly Message[],
): Message[] {
  const byId = new Map(current.map(message => [message.id, message]))
  for (const message of incoming) {
    const previous = byId.get(message.id)
    byId.set(message.id, previous?.readAt && !message.readAt
      ? { ...message, readAt: previous.readAt }
      : message)
  }

  return [...byId.values()].sort(
    (left, right) => left.seq - right.seq || left.id.localeCompare(right.id),
  )
}

export function mergeLatestMessagePage<Message extends SequencedMessage>(
  timeline: ConnectionMessageTimeline<Message>,
  connectionId: string,
  messages: readonly Message[],
  peerReadThroughSeq = 0,
): ConnectionMessageTimeline<Message> {
  const current = timeline.connectionId === connectionId
    ? timeline
    : createMessageTimeline<Message>(connectionId)
  return {
    ...current,
    messages: mergeSequencedMessages(current.messages, messages),
    peerReadThroughSeq: Math.max(current.peerReadThroughSeq, peerReadThroughSeq),
  }
}

export function mergeOlderMessagePage<Message extends SequencedMessage>(
  timeline: ConnectionMessageTimeline<Message>,
  connectionId: string,
  messages: readonly Message[],
  hasMore: boolean,
  peerReadThroughSeq = 0,
): ConnectionMessageTimeline<Message> {
  if (timeline.connectionId !== connectionId)
    return timeline
  return {
    ...timeline,
    messages: mergeSequencedMessages(timeline.messages, messages),
    olderHasMore: hasMore,
    peerReadThroughSeq: Math.max(timeline.peerReadThroughSeq, peerReadThroughSeq),
  }
}

export function messagesForConnection<Message extends SequencedMessage>(
  timeline: ConnectionMessageTimeline<Message>,
  connectionId: string,
): readonly Message[] {
  return timeline.connectionId === connectionId ? timeline.messages : []
}
